/**
 * PhotoGallery Sub-component
 *
 * Handles photo display with navigation, thumbnails, swipe support, and fullscreen mode.
 */
export class PhotoGallery {
    options;
    state;
    mainImage = null;
    thumbsContainer = null;
    counterEl = null;
    fullscreenViewer = null;
    fullscreenCarousel = null;
    fullscreenCounter = null;
    touchStartX = 0;
    touchStartY = 0;
    constructor(options) {
        this.options = options;
        this.state = {
            currentIndex: 0,
            photos: (options.photos || []).map(url => this.convertGoogleDriveUrl(url)),
            isFullscreen: false,
        };
    }
    /**
     * Convert Google Drive share URLs to direct image URLs
     */
    convertGoogleDriveUrl(url) {
        if (!url)
            return url;
        // Pattern 1: https://drive.google.com/file/d/{fileId}/view...
        const fileMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
        if (fileMatch) {
            return `https://drive.google.com/uc?export=view&id=${fileMatch[1]}`;
        }
        // Pattern 2: https://drive.google.com/open?id={fileId}
        const openMatch = url.match(/drive\.google\.com\/open\?id=([^&]+)/);
        if (openMatch) {
            return `https://drive.google.com/uc?export=view&id=${openMatch[1]}`;
        }
        // Pattern 3: Already a uc?export link - return as is
        if (url.includes('drive.google.com/uc?')) {
            return url;
        }
        return url;
    }
    render() {
        const { photos } = this.state;
        if (!photos.length) {
            // No placeholder box - off-market/TMK-only properties routinely have
            // no photos at all (per Todd 2026-10-24), and an empty gradient tile
            // reading "No photos available" just wastes space above the actual
            // property info. The container has no CSS footprint of its own, so
            // clearing it collapses to nothing rather than leaving a gap.
            this.options.container.innerHTML = '';
            return;
        }
        this.options.container.innerHTML = `
      <div class="sm-gallery">
        <div class="sm-gallery-main">
          <img src="${photos[0]}" alt="Property photo" />
          ${photos.length > 1 ? `
            <button class="sm-gallery-nav sm-gallery-prev" aria-label="Previous photo">&lsaquo;</button>
            <button class="sm-gallery-nav sm-gallery-next" aria-label="Next photo">&rsaquo;</button>
          ` : ''}
          <div class="sm-gallery-counter">${this.state.currentIndex + 1} / ${photos.length}</div>
          <button class="sm-gallery-fullscreen-btn" aria-label="Fullscreen">&#x26F6;</button>
        </div>
        ${photos.length > 1 ? `
          <div class="sm-gallery-thumbs">
            ${photos.map((photo, i) => `
              <img
                src="${photo}"
                class="sm-gallery-thumb${i === 0 ? ' active' : ''}"
                data-index="${i}"
                alt="Thumbnail ${i + 1}"
              />
            `).join('')}
          </div>
        ` : ''}
      </div>
    `;
        this.cacheElements();
        this.attachEventListeners();
    }
    cacheElements() {
        const gallery = this.options.container.querySelector('.sm-gallery-main');
        if (gallery) {
            this.mainImage = gallery.querySelector('img');
            this.counterEl = gallery.querySelector('.sm-gallery-counter');
        }
        this.thumbsContainer = this.options.container.querySelector('.sm-gallery-thumbs');
    }
    attachEventListeners() {
        const prevBtn = this.options.container.querySelector('.sm-gallery-prev');
        const nextBtn = this.options.container.querySelector('.sm-gallery-next');
        const fullscreenBtn = this.options.container.querySelector('.sm-gallery-fullscreen-btn');
        const mainContainer = this.options.container.querySelector('.sm-gallery-main');
        prevBtn?.addEventListener('click', () => this.navigate(-1));
        nextBtn?.addEventListener('click', () => this.navigate(1));
        fullscreenBtn?.addEventListener('click', () => this.openFullscreen());
        // Thumbnail clicks
        this.thumbsContainer?.querySelectorAll('.sm-gallery-thumb').forEach((thumb) => {
            thumb.addEventListener('click', (e) => {
                const index = parseInt(e.target.dataset.index || '0', 10);
                this.goTo(index);
            });
        });
        // Touch/swipe support
        if (mainContainer) {
            mainContainer.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
            mainContainer.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
        }
        // Keyboard navigation
        document.addEventListener('keydown', this.handleKeydown);
    }
    handleKeydown = (e) => {
        if (this.state.isFullscreen) {
            if (e.key === 'Escape') {
                this.closeFullscreen();
            }
            else if (e.key === 'ArrowLeft') {
                this.navigate(-1);
            }
            else if (e.key === 'ArrowRight') {
                this.navigate(1);
            }
        }
    };
    handleTouchStart(e) {
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
    }
    handleTouchEnd(e) {
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const deltaX = touchEndX - this.touchStartX;
        const deltaY = touchEndY - this.touchStartY;
        // Only swipe if horizontal movement is greater than vertical
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
            if (deltaX > 0) {
                this.navigate(-1);
            }
            else {
                this.navigate(1);
            }
        }
    }
    navigate(direction) {
        const { photos, currentIndex } = this.state;
        let newIndex = currentIndex + direction;
        if (newIndex < 0) {
            newIndex = photos.length - 1;
        }
        else if (newIndex >= photos.length) {
            newIndex = 0;
        }
        this.goTo(newIndex);
    }
    goTo(index) {
        const { photos } = this.state;
        if (index < 0 || index >= photos.length)
            return;
        this.state.currentIndex = index;
        // Update main image
        if (this.mainImage) {
            this.mainImage.src = photos[index];
        }
        // Update counter
        if (this.counterEl) {
            this.counterEl.textContent = `${index + 1} / ${photos.length}`;
        }
        // Update thumbnails
        this.thumbsContainer?.querySelectorAll('.sm-gallery-thumb').forEach((thumb, i) => {
            thumb.classList.toggle('active', i === index);
        });
        // Scroll thumbnail into view
        const activeThumbnail = this.thumbsContainer?.querySelector('.sm-gallery-thumb.active');
        activeThumbnail?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        // Update fullscreen if open
        if (this.state.isFullscreen && this.fullscreenCarousel) {
            const slide = this.fullscreenCarousel.children[index];
            slide?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'center' });
        }
        // Update fullscreen counter
        if (this.fullscreenCounter) {
            this.fullscreenCounter.textContent = `${index + 1} / ${photos.length}`;
        }
        // Callback
        this.options.onPhotoChange?.(index);
    }
    openFullscreen() {
        const { photos, currentIndex } = this.state;
        // Create fullscreen viewer
        this.fullscreenViewer = document.createElement('div');
        this.fullscreenViewer.className = 'sm-fullscreen-viewer active';
        // Check orientation
        const isPortrait = window.innerHeight > window.innerWidth;
        if (isPortrait) {
            this.fullscreenViewer.classList.add('portrait');
        }
        this.fullscreenViewer.innerHTML = `
      <button class="sm-fullscreen-close" aria-label="Close">&times;</button>
      <div class="sm-fullscreen-carousel">
        ${photos.map((photo, i) => `
          <div class="sm-fullscreen-slide">
            <img src="${photo}" alt="Photo ${i + 1}" />
          </div>
        `).join('')}
      </div>
      <div class="sm-fullscreen-counter">${currentIndex + 1} / ${photos.length}</div>
    `;
        document.body.appendChild(this.fullscreenViewer);
        document.body.style.overflow = 'hidden';
        this.fullscreenCarousel = this.fullscreenViewer.querySelector('.sm-fullscreen-carousel');
        this.fullscreenCounter = this.fullscreenViewer.querySelector('.sm-fullscreen-counter');
        // Scroll to current photo
        if (this.fullscreenCarousel) {
            const currentSlide = this.fullscreenCarousel.children[currentIndex];
            currentSlide?.scrollIntoView({ inline: 'center', block: 'center' });
            // Track scroll position to update counter
            this.fullscreenCarousel.addEventListener('scroll', this.handleFullscreenScroll);
        }
        // Close button
        this.fullscreenViewer.querySelector('.sm-fullscreen-close')?.addEventListener('click', () => {
            this.closeFullscreen();
        });
        // Click backdrop to close
        this.fullscreenViewer.addEventListener('click', (e) => {
            if (e.target === this.fullscreenViewer) {
                this.closeFullscreen();
            }
        });
        // Handle orientation changes
        window.addEventListener('resize', this.handleResize);
        this.state.isFullscreen = true;
    }
    handleFullscreenScroll = () => {
        if (!this.fullscreenCarousel)
            return;
        const { photos } = this.state;
        const isPortrait = this.fullscreenViewer?.classList.contains('portrait');
        const scrollPos = isPortrait ? this.fullscreenCarousel.scrollTop : this.fullscreenCarousel.scrollLeft;
        const slideSize = isPortrait
            ? this.fullscreenCarousel.children[0]?.offsetHeight || 0
            : this.fullscreenCarousel.children[0]?.offsetWidth || 0;
        if (slideSize > 0) {
            const newIndex = Math.round(scrollPos / slideSize);
            if (newIndex !== this.state.currentIndex && newIndex >= 0 && newIndex < photos.length) {
                this.state.currentIndex = newIndex;
                if (this.fullscreenCounter) {
                    this.fullscreenCounter.textContent = `${newIndex + 1} / ${photos.length}`;
                }
                // Update main gallery
                this.updateMainGallery(newIndex);
            }
        }
    };
    handleResize = () => {
        if (!this.fullscreenViewer)
            return;
        const isPortrait = window.innerHeight > window.innerWidth;
        this.fullscreenViewer.classList.toggle('portrait', isPortrait);
    };
    updateMainGallery(index) {
        if (this.mainImage) {
            this.mainImage.src = this.state.photos[index];
        }
        if (this.counterEl) {
            this.counterEl.textContent = `${index + 1} / ${this.state.photos.length}`;
        }
        this.thumbsContainer?.querySelectorAll('.sm-gallery-thumb').forEach((thumb, i) => {
            thumb.classList.toggle('active', i === index);
        });
    }
    closeFullscreen() {
        if (this.fullscreenViewer) {
            this.fullscreenCarousel?.removeEventListener('scroll', this.handleFullscreenScroll);
            window.removeEventListener('resize', this.handleResize);
            this.fullscreenViewer.remove();
            this.fullscreenViewer = null;
            this.fullscreenCarousel = null;
            this.fullscreenCounter = null;
            document.body.style.overflow = '';
        }
        this.state.isFullscreen = false;
    }
    updatePhotos(photos) {
        this.state.photos = photos.map(url => this.convertGoogleDriveUrl(url));
        this.state.currentIndex = 0;
        this.render();
    }
    getCurrentIndex() {
        return this.state.currentIndex;
    }
    destroy() {
        document.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('resize', this.handleResize);
        this.closeFullscreen();
        this.options.container.innerHTML = '';
    }
}
//# sourceMappingURL=PhotoGallery.js.map